import type { jsPDF } from 'jspdf';
import type { PpePersonalCard } from '@/entities/ppe/model/ppeSchema';
import wixMadeforTextTtfUrl from '@/assets/fonts/WixMadeforText/WixMadeforText-Regular.ttf';
import { colors } from '@/shared/constants/colors';

const FONT_FILE = 'WixMadeforText-Regular.ttf';
const FONT_NAME = 'WixMadeforText';

let fontBase64Promise: Promise<string> | null = null;

function hexToRgbTuple(hex: string): [number, number, number] | null {
  const normalized = hex.trim().replace(/^#/, '');
  if (!/^[\da-fA-F]{6}$/.test(normalized)) return null;
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return [r, g, b];
}

const PRIMARY_HEAD_FILL_RGB: [number, number, number] = hexToRgbTuple(colors.orange.o500) ?? [20, 89, 105];

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as unknown as number[]);
  }
  return btoa(binary);
}

async function loadFontBase64(): Promise<string> {
  const res = await fetch(wixMadeforTextTtfUrl);
  if (!res.ok) throw new Error(`Шрифт не загружен: ${res.status}`);
  return arrayBufferToBase64(await res.arrayBuffer());
}

export async function ensureCyrillicFont(doc: jsPDF): Promise<void> {
  if (!fontBase64Promise) fontBase64Promise = loadFontBase64();
  const base64 = await fontBase64Promise;
  doc.addFileToVFS(FONT_FILE, base64);
  doc.addFont(FONT_FILE, FONT_NAME, 'normal', undefined, 'Identity-H');
  doc.setFont(FONT_NAME, 'normal');
}

export async function exportPdfTable(params: {
  title: string;
  fileSlug: string;
  head: string[][];
  body: (string | number)[][];
  headFillRgb?: [number, number, number];
}): Promise<void> {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const doc = new jsPDF();
  await ensureCyrillicFont(doc);
  doc.setFontSize(14);
  doc.text(params.title, 14, 18);
  doc.setFontSize(9);
  doc.text(`Дата формирования: ${new Date().toLocaleDateString('ru-RU')}`, 14, 25);
  const fill = params.headFillRgb ?? PRIMARY_HEAD_FILL_RGB;
  autoTable(doc, {
    startY: 30,
    head: params.head,
    body: params.body,
    styles: { font: FONT_NAME, fontStyle: 'normal', fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: fill, font: FONT_NAME, fontStyle: 'normal' },
  });
  doc.save(`${params.fileSlug.replace(/\s+/g, '_')}.pdf`);
}

export async function exportPpePersonalCardPdf(card: PpePersonalCard): Promise<void> {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const doc = new jsPDF();
  await ensureCyrillicFont(doc);
  doc.setFontSize(14);
  doc.text('Личная карточка учёта СИЗ', 14, 18);
  doc.setFontSize(9);
  let y = 26;
  const line = (t: string) => {
    doc.text(t, 14, y);
    y += 5;
  };
  line(`Сотрудник: ${card.employeeName}`);
  line(`Таб. №: ${card.tabNumber}`);
  line(`Профессия: ${card.position}`);
  line(`Рабочее место: ${card.workplace}`);
  line(`Класс УТ: ${card.conditionClass}`);
  line(`Назначение норм: ${card.assignedBy}`);
  line(`Дата формирования: ${new Date().toLocaleDateString('ru-RU')}`);
  autoTable(doc, {
    startY: y + 4,
    head: [['№', 'Наименование СИЗ', 'Норма', 'Выдано']],
    body: card.items.map((it, i) => [i + 1, it.ppeName, it.norm, it.issued ?? '—']),
    styles: { font: FONT_NAME, fontStyle: 'normal', fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: PRIMARY_HEAD_FILL_RGB, font: FONT_NAME, fontStyle: 'normal' },
  });
  doc.save(`Личная_карточка_СИЗ_${card.tabNumber}.pdf`);
}

export function downloadUtf8Csv(filename: string, header: string, rows: string[][]): void {
  const BOM = '\uFEFF';
  const body = rows.map((r) => r.join(';')).join('\n');
  const blob = new Blob([BOM + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}
